<template>
  <div :class="[classes, {'form-item__field--full': fullWidth }]" class="form-item__field">
    <div class="input-group input-group-booking-detail input-height">
      <label class="input-label input-group-addon" :class="highlight ? 'input-highlight' : null">{{ label }}</label>
      <div v-if="!editable" class="form-control input-height input-container">
        <p v-if="fieldType === 'date'">
          <span v-if="showBookingTz">
            {{ localFieldBookingTzDate | date($user.date_format_preference) | nullable }}
          </span>
          <span v-else>
            {{ localField | date($user.date_format_preference) | nullable }}
          </span>
          <span v-if="booking.timezone !== $user.timezone && !tzDateMatch && showTz && localField">
            ({{ localFieldTzDate | date($user.date_format_preference) | nullable }} {{ $user.timezone }})
          </span>
          {{ postText }}
        </p>
        <p v-else-if="fieldType === 'time'">
          {{ localField | time | nullable }}
          <span v-if="booking.timezone !== $user.timezone && showTz && localField">
            ({{ localFieldTzTime | time }} {{ $user.timezone }})
          </span>
        </p>
        <p v-else-if="fieldType !== 'multi-select' && currency" :class="highlight ? 'highlight-text' : null" style="width: 100%">{{ preText }}{{ value ? value : localField | nullable | currency(booking.currency) }}{{ postText }}</p>
        <p v-else-if="fieldType !== 'multi-select'" :class="highlight ? 'highlight-text' : null" style="width: 100%">{{ preText }}{{ value ? value : localField | nullable }}{{ postText }}</p>
        <div v-else-if="fieldType === 'multi-select'" style="display: flex; flex-wrap: wrap;">
            <span class="tag" style="margin: 2px 5px; text-transform: capitalize;" v-for="item in localField" :key="item.id">
              {{ item.database_name ? item.database_name : item.name }}<span v-if="item.other_text"> ({{ item.type | humanize | nullable }}) - {{ item.other_text | nullable }}</span>
            </span>
        </div>
      </div>
      <div v-else class="form-control input-height input-container" :style="fieldType === 'textarea' ? 'flex-direction: column; justify-content: center;' : null">
        <p v-if="!editing && fieldType === 'date'">
          <span v-if="showBookingTz">
            {{ localFieldBookingTzDate | date($user.date_format_preference) | nullable }}
          </span>
          <span v-else>
            {{ localField | date($user.date_format_preference) | nullable }}
          </span>
          <span v-if="booking.timezone !== $user.timezone && !tzDateMatch && showTz && localField">
            ({{ localFieldTzDate | date($user.date_format_preference) | nullable }} {{ $user.timezone }})
          </span>
          {{ postText }}
        </p>
        <p v-else-if="!editing && fieldType === 'time'">
          {{ localField | time | nullable }}
          <span v-if="booking.timezone !== $user.timezone && showTz && localField && $vlastMode && booking.address !== '' && booking.appearance_type !== 'online'">
            ({{ localFieldTzTime | time }} {{ $user.timezone }})
          </span>
          <span v-if="booking.timezone !== $user.timezone && showTz && localField && !$vlastMode">
            ({{ localFieldTzTime | time }} {{ $user.timezone }})
          </span>
        </p>
        <p v-else-if="!editing && fieldType === 'select' && otherSelected">{{ localField }} - {{ localOtherField }}</p>
        <div v-else-if="!editing && fieldType === 'textarea' && html" class="pre-line" v-html="localField"></div>
        <div v-else-if="!editing && fieldType === 'textarea'" class="pre-line">{{ localField }}</div>
        <div v-else-if="!editing && fieldType === 'file'"><a download="true" target="_blank" :href="options.fileUrl">{{ value ? value : localField | nullable }}</a></div>
        <div v-else-if="!editing && fieldType === 'link'"><a target="_blank" :href="value ? value : localField | nullable">{{ value ? value : localField | nullable }}</a></div>
        <p v-else-if="!editing && fieldType !== 'multi-select'" :class="highlight ? 'highlight-text' : null" style="width: 100%">{{ preText }}{{ value ? value : localField | nullable }}{{ postText }}</p>
        <div v-else-if="!editing && fieldType === 'multi-select'" style="display: flex; flex-wrap: wrap;">
            <span class="tag" style="margin: 2px 5px; text-transform: capitalize;" v-for="item in localField" :key="item.id">
              {{ item.database_name ? item.database_name : item.name }}<span v-if="item.other_text"> ({{ item.type | humanize | nullable }}) - {{ item.other_text | nullable }}</span>
            </span>
        </div>
        <textarea
          v-else-if="fieldType === 'textarea'"
          v-model="localField"
          :name="field"
          :type="fieldType"
          class="form-control"
          style="width: 100%;"
          :rows="rows"/>
        <selectize
          v-else-if="fieldType === 'select'"
          class="selectize selectize-single"
          style="width: 100%;"
          :name="field"
          :disable-delete="true"
          v-model="localField"
          :options="options">
        </selectize>
        <flat-pickr
          v-else-if="fieldType === 'date'"
          v-model="localField"
          :config="dateConfig"
          class="form-control"
          placeholder="DD/MM/YYYY"
          :name="field">
        </flat-pickr>
        <flat-pickr
          v-else-if="fieldType === 'time'"
          v-model="localField"
          :config="timeConfig"
          class="form-control"
          placeholder="DD/MM/YYYY"
          :name="field">
        </flat-pickr>
        <booking-field-opportunity-types v-model="localField" v-else-if="fieldType === 'multi-select'" :type="field" :types="multiSelectTypes" :booking="booking" :property-mutator="propertyMutator"/>
        <div v-else-if="fieldType === 'location'" style="width: 100%;">
          <gmap-autocomplete
            name="location"
            class="form-control"
            placeholder="1 Brunton Avenue, Richmond, Victoria"
            :value="booking.address"
            :options="{
                selectFirstOnEnter: true,
                strictBounds: true,
                bounds: {
                  north: -9.187026,
                  south: -54.833766,
                  east: 159.287222,
                  west: 110.951034
                }
              }"
            @place_changed="handleAddressChangeCallback"
            @input.native="handleResetAddressCallback"
            @keydown.native.enter.prevent/>

          <input type="hidden" name="street_address" :value="stateStreetAddress"/>
          <input type="hidden" name="suburb" :value="stateSuburb"/>
          <input type="hidden" name="state" :value="stateState"/>
          <input type="hidden" name="country" :value="stateCountry"/>
          <input type="hidden" name="postcode" :value="statePostcode"/>
          <input type="hidden" name="area" :value="stateArea"/>
          <input type="hidden" name="latitude" :value="stateLatitude"/>
          <input type="hidden" name="longitude" :value="stateLongitude"/>
        </div>
        <input
          v-else
          v-model="localField"
          :name="field"
          :type="fieldType"
          class="form-control"/>
        <slot name="after-info"/>
      </div>
      <div class="form-control" v-if="editing && fieldType === 'select' && otherSelected && otherField">
        <input
          v-model="localOtherField"
          :name="otherField"
          type="text"
          placeholder="Other Details..."
          class="form-control"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Selectize from '../ext/codium/CodiumSelectize'
import BookingFieldOpportunityTypes from './BookingFieldOpportunityTypes'
import FlatPickr from 'vue-flatpickr-component'
import moment from 'moment-timezone'
import { BookingStateMapper as stateMapper } from 'pickstar-common'
import { each } from 'lodash'

export default {
  mixins: [stateMapper],
  data () {
    return {
      dateConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        wrap: true,
        disable: [
          (date) => {
            if (this.$userIsAdmin) {
              return false
            }

            return moment(date).isBefore(moment().startOf('day'))
          }
        ]
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i:S',
        altFormat: 'h:i K',
        altInput: true,
        wrap: true,
        onOpen: (selectedTimes, timeString, flatPickerInstance) => {
          if (selectedTimes.length === 0) {
            flatPickerInstance.setDate('12:00', true)
          }
        }
      }
    }
  },

  components: {
    BookingFieldOpportunityTypes,
    Selectize,
    FlatPickr
  },

  props: {
    html: {
      required: false,
      type: Boolean,
      default: false
    },
    field: {
      required: true,
      type: String
    },
    value: {
      required: false
    },
    label: {
      required: true,
      type: String
    },
    fieldType: {
      required: false,
      default: 'text',
      type: String
    },
    rows: {
      required: false,
      default: 6,
      type: Number
    },
    options: {
      required: false,
      default: () => { return [] },
      type: Array | Object
    },
    classes: {
      required: false,
      default: '',
      type: String
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    multiSelectTypes: {
      required: false,
      default: null,
      type: Array
    },
    preText: {
      required: false,
      default: null,
      type: String
    },
    postText: {
      required: false,
      default: null,
      type: String
    },
    highlight: {
      required: false,
      default: false,
      type: Boolean
    },
    booking: {
      required: true,
      type: Object
    },
    propertyMutator: {
      required: true,
      type: Function
    },
    editable: {
      required: false,
      default: true
    },
    type: {
      required: false,
      default: ''
    },
    otherField: {
      required: false,
      default: null
    },
    currency: {
      required: false,
      default: false
    },
    showTz: {
      required: false,
      default: true
    },
    showBookingTz: {
      required: false,
      default: false
    }
  },

  methods: {
    /**
     * Handle resetting the address if target input value is empty.
     *
     * @param object event
     *
     * @return void
     */
    handleResetAddressCallback (event) {
      if (event.target.value === null || event.target.value === '') {
        this.stateEstablishment = ''
        this.stateStreetAddress = ''
        this.stateSuburb = ''
        this.statePostcode = ''
        this.stateArea = ''
        this.stateState = ''
        this.stateCountry = ''
        this.stateLatitude = ''
        this.stateLongitude = ''
      }
    },

    /**
     * Handle the address changing from Google places.
     *
     * Sets the appropriate state for each of the address components.
     *
     * @param object place
     *
     * @return void
     */
    handleAddressChangeCallback (place) {
      let components = {}

      each(place.address_components, component => {
        each(component.types, type => {
          components[type] = component.long_name
        })
      })

      if (place.types.some(type => ['establishment', 'point_of_interest'].indexOf(type) > -1)) {
        this.stateEstablishment = place.name || ''
      } else {
        this.stateEstablishment = ''
      }
      this.stateStreetAddress = `${components.street_number || ''} ${components.route || ''}`.trim()
      this.stateSuburb = components.locality || ''
      this.stateArea = components.administrative_area_level_2
      this.statePostcode = components.postal_code || ''
      this.stateState = components.administrative_area_level_1 || ''
      this.stateCountry = components.country || ''
      this.stateLatitude = place.geometry.location.lat()
      this.stateLongitude = place.geometry.location.lng()
    }
  },

  computed: {
    ...mapState('editing', ['editing']),

    localField: {
      get () {
        return this.booking[this.field]
      },
      set (value) {
        this.propertyMutator({
          property: this.field,
          value
        })
      }
    },

    localOtherField: {
      get () {
        if (this.otherField) {
          return this.booking[this.otherField]
        }
      },
      set (value) {
        if (this.otherField) {
          this.propertyMutator({
            property: this.otherField,
            value
          })
        }
      }
    },

    localFieldBookingTzDate () {
      if (this.fieldType === 'date') {
        return moment.utc(this.localField).tz(this.booking.timezone)
      }
      return null
    },

    localFieldTzDate () {
      if (this.fieldType === 'date') {
        return moment.utc(this.localField).tz(this.$user.timezone)
      }
      return null
    },

    localFieldTzTime () {
      if (this.fieldType === 'time') {
        return moment.tz(this.localField, 'HH:mm:ss', this.booking.timezone).tz(this.$user.timezone).format('HH:mm:ss')
      }
      return null
    },

    tzDateMatch () {
      if (this.showBookingTz) {
        return moment.utc(this.localField).tz(this.booking.timezone).date() === moment.utc(this.localField).tz(this.$user.timezone).date()
      }
      return moment.utc(this.localField).date() === moment.utc(this.localField).tz(this.$user.timezone).date()
    },

    otherSelected () {
      return this.localField === 'Other'
    }
  }
}
</script>

<style scoped lang="scss">
.input-label {
  overflow-wrap: break-word;
  white-space: pre-wrap;
  margin-bottom: 0;
}
</style>
